import { useState, useEffect } from "react"
import { Icon } from "../trust-center/ui/primitives"
import api from "../services/api"
import { sanitizeTextInput, sanitizeEmailInput, sanitizeDigitsInput } from "../utils/sanitizeInput"
import { isAdminIpDenied, getAdminIpDeniedMessage } from "../utils/adminIpError"
import { resetAdminIpDeniedGuard } from "../services/sessionGuard"

const TERMS_URL = "https://darwinbox.com/legal-terms-and-conditions-v4/in"
const OTP_VALIDITY_SECONDS = 300
const MAX_OTP_RESENDS = 3

/**
 * The single email -> OTP -> registration flow used everywhere a user needs to
 * authenticate (plain sign-in and "request access"). Previously this logic was
 * duplicated with two different orderings across TrustLoginModal and AuthModal;
 * consolidating it here means both entry points now behave identically and
 * share one set of bugs/fixes instead of two.
 *
 * Calls onAuthenticated(user) once a session has been established.
 */
export default function AuthSteps({ initialEmail = "", onAuthenticated, onCancel, onStepChange }) {
    const [step, setStep] = useState("form") // form -> otp -> register
    const [email, setEmail] = useState(initialEmail)
    const [otp, setOtp] = useState("")
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [company, setCompany] = useState("")
    const [jobTitle, setJobTitle] = useState("")
    const [termsAccepted, setTermsAccepted] = useState(false)
    const [inlineError, setInlineError] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [otpBlocked, setOtpBlocked] = useState(false)
    const [otpBlockedMsg, setOtpBlockedMsg] = useState("")
    const [resendLoading, setResendLoading] = useState(false)
    const [resendCooldown, setResendCooldown] = useState(0)
    const [resendRemaining, setResendRemaining] = useState(MAX_OTP_RESENDS)
    const [otpExpiryTime, setOtpExpiryTime] = useState(0)
    const [otpVerifyRemaining, setOtpVerifyRemaining] = useState(3)

    // Lets the modal shell (rendered by the parent) know which step is active,
    // so it can widen itself just for the registration step.
    useEffect(() => {
        onStepChange?.(step)
    }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (resendCooldown <= 0) return
        const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
        return () => clearTimeout(timer)
    }, [resendCooldown])

    useEffect(() => {
        if (otpExpiryTime <= 0) return
        const timer = setTimeout(() => setOtpExpiryTime((t) => t - 1), 1000)
        return () => clearTimeout(timer)
    }, [otpExpiryTime])

    const sendOtp = async () => {
        resetAdminIpDeniedGuard()
        await api.post("/auth/send-otp", { email: email.trim() })
        setResendRemaining(MAX_OTP_RESENDS)
        setOtpExpiryTime(OTP_VALIDITY_SECONDS)
        setOtpVerifyRemaining(3)
        setStep("otp")
    }

    const handleResendOtp = async () => {
        if (resendLoading || resendCooldown > 0 || otpBlocked || resendRemaining <= 0) return
        setInlineError("")
        try {
            setResendLoading(true)
            await api.post("/auth/send-otp", { email: email.trim() })
            setResendRemaining((r) => r - 1)
            setResendCooldown(30)
            setOtpExpiryTime(OTP_VALIDITY_SECONDS)
            setOtp("")
        } catch (err) {
            const data = err.response?.data
            if (isAdminIpDenied(err)) {
                setInlineError(getAdminIpDeniedMessage(err))
            } else if (err.response?.status === 429) {
                setOtpBlocked(true)
                setOtpBlockedMsg(data?.error || "Maximum resend limit reached. Please try again later.")
            } else {
                setInlineError(data?.error || "Failed to resend verification code.")
            }
        } finally {
            setResendLoading(false)
        }
    }

    const handleSignIn = async (e) => {
        e.preventDefault()
        setInlineError("")
        const trimmed = email.trim()
        if (!trimmed) {
            setInlineError("Please enter your work email.")
            return
        }
        try {
            setIsSubmitting(true)
            await sendOtp()
        } catch (err) {
            setInlineError(
                isAdminIpDenied(err)
                    ? getAdminIpDeniedMessage(err)
                    : (err.response?.data?.error || "Unable to send verification code. Please try again.")
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const completeRegistration = async () => {
        const registration = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            company: company.trim(),
            title: jobTitle.trim(),
            reason: "I'm a prospective customer",
            termsAccepted,
            marketingUpdates: false,
        }
        await api.post("/auth/register-prospect", { email: email.trim(), ...registration })
        const res = await api.post("/auth/verify-otp", {
            email: email.trim(),
            otp,
            registration,
        })
        if (res.data.needsRegistration) {
            throw new Error("Registration could not be completed. Please try again.")
        }
        return res.data.user
    }

    const handleVerifyOtp = async (e) => {
        e.preventDefault()
        setInlineError("")
        try {
            setIsSubmitting(true)
            const res = await api.post("/auth/verify-otp", { email: email.trim(), otp })
            if (res.data.needsRegistration) {
                setStep("register")
                return
            }
            onAuthenticated?.(res.data.user)
        } catch (err) {
            const data = err.response?.data
            if (isAdminIpDenied(err)) {
                setInlineError(getAdminIpDeniedMessage(err))
            } else if (err.response?.status === 429) {
                setOtpBlocked(true)
                setOtpBlockedMsg(data?.error || "Too many attempts. Please try again later.")
            } else {
                if (data?.attemptsRemaining !== undefined) setOtpVerifyRemaining(data.attemptsRemaining)
                setInlineError(data?.message || data?.error || "Invalid verification code.")
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleRegister = async (e) => {
        e.preventDefault()
        setInlineError("")
        if (!firstName.trim() || !lastName.trim() || !company.trim()) {
            setInlineError("Please fill in all required fields.")
            return
        }
        if (!termsAccepted) {
            setInlineError("Please accept the terms to continue.")
            return
        }
        try {
            setIsSubmitting(true)
            const user = await completeRegistration()
            onAuthenticated?.(user)
        } catch (err) {
            setInlineError(
                isAdminIpDenied(err)
                    ? getAdminIpDeniedMessage(err)
                    : (err.response?.data?.error || err.message || "Registration failed. Please try again.")
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            {inlineError && <div className="modal__error" role="alert">{inlineError}</div>}

            {step === "form" && (
                <>
                    <form onSubmit={handleSignIn}>
                        <div className="field">
                            <label>Work email <span style={{ color: "#DE350B" }}>*</span></label>
                            <input
                                type="email"
                                required
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
                                autoFocus
                            />
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            {onCancel && (
                                <button type="button" className="btn btn--outline" onClick={onCancel}>Cancel</button>
                            )}
                            <button className="btn btn--primary btn--block" type="submit" disabled={isSubmitting}>
                                <Icon name="arrow-right" size={18} />{isSubmitting ? "Sending code…" : "Continue"}
                            </button>
                        </div>
                    </form>
                    <p className="modal__foot">
                        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                            Terms and Conditions
                        </a>
                    </p>
                </>
            )}

            {step === "otp" && (
                <form onSubmit={handleVerifyOtp}>
                    <p style={{ color: "var(--text-neutral-secondary)", fontSize: "var(--font-size-body-m)", marginBottom: 16 }}>
                        Enter the verification code sent to <strong>{email}</strong>
                    </p>
                    <div className="field">
                        <label>Verification code <span style={{ color: "#DE350B" }}>*</span></label>
                        <input
                            type="text"
                            required
                            placeholder="123456"
                            value={otp}
                            onChange={(e) => setOtp(sanitizeDigitsInput(e.target.value))}
                            maxLength={6}
                            autoFocus
                            disabled={otpExpiryTime <= 0}
                        />
                        {otpExpiryTime > 0 ? (
                            <p style={{
                                fontSize: "var(--font-size-body-s)",
                                color: otpExpiryTime <= 60 ? "#dc2626" : otpExpiryTime <= 120 ? "#f59e0b" : "var(--text-neutral-secondary)",
                                marginTop: 6,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}>
                                <span style={{
                                    display: "inline-block",
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: otpExpiryTime <= 60 ? "#dc2626" : otpExpiryTime <= 120 ? "#f59e0b" : "#22c55e",
                                    animation: otpExpiryTime <= 60 ? "pulse 1s infinite" : "none",
                                }} />
                                Code expires in {Math.floor(otpExpiryTime / 60)}:{String(otpExpiryTime % 60).padStart(2, "0")}
                            </p>
                        ) : (
                            <p style={{ fontSize: "var(--font-size-body-s)", color: "#dc2626", marginTop: 6, fontWeight: 600 }}>
                                Code expired. Please request a new code.
                            </p>
                        )}
                        {!otpBlocked && otpVerifyRemaining < 3 && (
                            <p style={{ fontSize: "var(--font-size-body-s)", color: otpVerifyRemaining <= 1 ? "#dc2626" : "#f59e0b", marginTop: 6, fontWeight: 600 }}>
                                {otpVerifyRemaining} attempt{otpVerifyRemaining !== 1 ? "s" : ""} remaining
                            </p>
                        )}
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <button className="btn btn--primary btn--block" type="submit" disabled={isSubmitting || otpBlocked || otpExpiryTime <= 0}>
                            {isSubmitting ? "Verifying…" : "Verify & continue"}
                        </button>
                        <p style={{ margin: "12px 0 0", textAlign: "center" }}>
                            <button
                                type="button"
                                className="modal__foot-link"
                                onClick={handleResendOtp}
                                disabled={resendLoading || resendCooldown > 0 || otpBlocked || resendRemaining <= 0}
                                style={
                                    resendLoading || resendCooldown > 0 || otpBlocked || resendRemaining <= 0
                                        ? { color: "var(--text-neutral-muted)", cursor: "not-allowed", textDecoration: "none" }
                                        : undefined
                                }
                            >
                                {resendLoading ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : resendRemaining <= 0 ? "Resend limit reached" : `Resend code (${resendRemaining} left)`}
                            </button>
                        </p>
                    </div>
                    {otpBlocked && (
                        <p style={{ color: "#b91c1c", fontSize: "var(--font-size-body-s)", marginTop: 10 }}>{otpBlockedMsg}</p>
                    )}
                    <style>{`
                        @keyframes pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.5; }
                        }
                    `}</style>
                </form>
            )}

            {step === "register" && (
                <form onSubmit={handleRegister}>
                    <p style={{ color: "var(--text-neutral-secondary)", fontSize: "var(--font-size-body-m)", marginBottom: 16 }}>
                        Complete your profile for <strong>{email}</strong> to finish signing in.
                    </p>
                    <div className="register-grid">
                        <div className="field" style={{ marginBottom: 0 }}>
                            <label>First name <span style={{ color: "#DE350B" }}>*</span></label>
                            <input type="text" required value={firstName} onChange={(e) => setFirstName(sanitizeTextInput(e.target.value))} />
                        </div>
                        <div className="field" style={{ marginBottom: 0 }}>
                            <label>Last name <span style={{ color: "#DE350B" }}>*</span></label>
                            <input type="text" required value={lastName} onChange={(e) => setLastName(sanitizeTextInput(e.target.value))} />
                        </div>
                        <div className="field" style={{ marginBottom: 0 }}>
                            <label>Company <span style={{ color: "#DE350B" }}>*</span></label>
                            <input type="text" required value={company} onChange={(e) => setCompany(sanitizeTextInput(e.target.value))} />
                        </div>
                        <div className="field" style={{ marginBottom: 0 }}>
                            <label>Job title <span className="field__optional">(optional)</span></label>
                            <input type="text" value={jobTitle} onChange={(e) => setJobTitle(sanitizeTextInput(e.target.value))} placeholder="e.g. Security Engineer" />
                        </div>
                    </div>
                    <label className="field-checkbox" style={{ marginBottom: 20 }}>
                        <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                        <span>
                            I have read and agree to the{" "}
                            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
                                Terms and Conditions
                            </a>
                            . <span style={{ color: "#DE350B" }}>*</span>
                        </span>
                    </label>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button type="button" className="btn btn--outline" onClick={() => setStep("otp")}>Back</button>
                        <button className="btn btn--primary btn--block" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "Creating account…" : "Complete sign in"}
                        </button>
                    </div>
                </form>
            )}
        </>
    )
}
